/*LIBRARY*/
import { useState, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  ArcElement,
  RadarController,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
  BubbleController,
  ScatterController,
  PieController,
} from "chart.js";

/*PICTURE*/

/*FUNCTION*/

/*DATA*/

/*CONST*/
ChartJS.register(
  PieController,
  ArcElement,
  Tooltip,
  Legend,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  TimeScale,
  Filler,
  BubbleController,
  ScatterController
);

const CHART_CARD = ({ GRAPH_RESULT, TITLE_KEY }) => {
  const {
    label_list,
    data_list,
    color_list,
    graph_type: defaultType,
  } = GRAPH_RESULT;

  const title = GRAPH_RESULT[TITLE_KEY];
  const graphTypes = ["bar", "line", "doughnut", "pie", "polarArea"];
  const [currentType, setCurrentType] = useState(defaultType || "bar");

  const isCircleChart = ["pie", "doughnut", "polarArea"].includes(currentType);
  const isLongChart = ["bar", "line", "area"].includes(currentType);
  const chartHeight = isLongChart ? 150 : undefined;

  // ambil angka langsung, kalau object ambil field y
  const numericData = data_list.map((d) =>
    typeof d === "object" && d !== null ? d.y : d
  );
  const total = numericData.reduce((sum, val) => sum + val, 0);

  const chartData = useMemo(
    () => ({
      labels: label_list,
      datasets: [
        {
          label: title,
          data: numericData,
          backgroundColor: color_list,
          borderColor: color_list,
          borderWidth: 1,
          fill: currentType === "area",
          tension: ["line", "area"].includes(currentType) ? 0.3 : undefined,
        },
      ],
    }),
    [label_list, numericData, color_list, title, currentType]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: !isLongChart,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
      scales: isLongChart
        ? {
            x: {
              beginAtZero: true,
              ticks: { display: currentType === "line" },
            },
            y: {
              beginAtZero: true,
            },
          }
        : {},
    }),
    [isLongChart, currentType]
  );

  const customLegend = (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "8px",
        marginTop: isLongChart || isCircleChart ? "16px" : "0px",
        maxHeight: "200px",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <table>
        <tbody>
          {label_list.map((label, index) => {
            const count = numericData[index];
            const percentage =
              total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
            return (
              <tr key={index} style={{ whiteSpace: "nowrap" }}>
                <td
                  style={{
                    verticalAlign: "top",
                    paddingRight: "8px",
                    textAlign: "center",
                    width: "30px",
                  }}
                >
                  <div
                    style={{
                      width: "15px",
                      height: "15px",
                      backgroundColor: color_list[index] || "transparent",
                      margin: "0 auto",
                    }}
                  />
                </td>
                <td style={{ verticalAlign: "top" }}>
                  <div style={{ display: "block" }}>
                    {label} <br />({count} ~ {percentage}%)
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );

  return (
    <main className="container background_white margin_bottom margin_top">
      <p className="text_bold margin_bottom">{title}</p>

      <section>
        {graphTypes.map((type) => (
          <button
            key={type}
            onClick={() => setCurrentType(type)}
            className={`button_small ${
              currentType === type
                ? "bg_black text_white"
                : "outline_black text_black"
            } margin_left margin_bottom`}
          >
            {type}
          </button>
        ))}
      </section>

      {isCircleChart ? (
        <main
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <section style={{ width: "38%", maxWidth: "38%" }}>
            <Chart
              key={currentType}
              type={currentType}
              data={chartData}
              options={chartOptions}
              style={{ height: chartHeight, maxHeight: chartHeight }}
            />
          </section>
          <section style={{ width: "58%", maxWidth: "58%" }}>
            {customLegend}
          </section>
        </main>
      ) : (
        <main style={{ width: "100%" }}>
          <div
            style={{
              overflowX: "auto",
              paddingBottom: "1rem",
            }}
          >
            <div style={{ minWidth: `${label_list.length * 30}px` }}>
              <Chart
                key={currentType}
                type={currentType}
                data={chartData}
                options={chartOptions}
                style={{ height: chartHeight, maxHeight: chartHeight }}
              />
            </div>
          </div>
          {customLegend}
        </main>
      )}
    </main>
  );
};

export default CHART_CARD;
